/**
 * 영업진행현황 메뉴 — 비상벨 큰 탭.
 *
 * 운영 엑셀 (현진ICT 비상벨 자료) 18 컬럼 1:1 보존.
 * sales 권한자가 본인 화면에 표시할 컬럼을 토글로 선택 (DB app_user.preferences 영구 저장).
 *
 * 매출 합산 정책: 매출세금계산서(invoice_date) 발행된 건만 매출분석에 카운트.
 *   ↳ 본 화면은 모든 행 표시, 화면 상단 [📜 발행만] 토글로 invoice_date NOT NULL 만 보기.
 */

import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as bellApi from '../lib/bell-api';
import * as prefsApi from '../lib/preferences-api';
import { SearchBar } from './SearchBar';
import { ResetButton } from './ResetButton';
import { ExcelModeGroup } from './ExcelModeButton';
import { useExcelModeTable } from '../lib/useExcelModeTable';
import * as quickApi from '../lib/quick-revenue-api';
import { syncAllData } from '../lib/invalidate';
import type { EmergencyBellRow, UserPreferences } from '../lib/types';
import {
  type Column,
  ColumnHeader,
  applyColState,
  useColState,
} from './DataTable';
import { LastUpdateBadge } from './LastUpdateBadge';
import { MailSendButton } from './MailSendButton';
import { VatNote } from './VatNote';
import { useAuth } from '../lib/auth-context';
import { exportRowsToExcel, dateStamp } from '../lib/excelExport';
import { listAttachments, previewAttachment } from '../lib/attachment-api';
import type { BellSalesEditInitial } from './BellSalesQuickModal';

const BellAttachmentDownloadModal = lazy(() =>
  import('./AttachmentDownloadModal').then((m) => ({
    default: (props: { parentId: string; onClose: () => void }) => (
      <m.AttachmentDownloadModal
        parentKind="bell"
        parentId={props.parentId}
        kind="분할납품요구서"
        title="📄 분납서 선택"
        onClose={props.onClose}
      />
    ),
  })),
);
import type { BellStatus } from '../lib/quick-revenue-api';
import { mailSubject } from '../lib/mail-subject';

// 모달은 lazy — 초기 번들에서 분리. ✎ 수정 시 처음 로드.
const BellSalesQuickModal = lazy(() =>
  import('./BellSalesQuickModal').then((m) => ({ default: m.BellSalesQuickModal })),
);

const VALID_BELL_STATUS: BellStatus[] = ['종결', '검사검수요청', '취소', '매입계산서 보류'];
function toBellStatus(v: string | null): BellStatus | null {
  if (!v) return null;
  return (VALID_BELL_STATUS as readonly string[]).includes(v) ? (v as BellStatus) : null;
}

const FETCH_LIMIT = 10000;

const STATUS_VALUES = ['종결', '검사검수요청', '취소', '매입계산서 보류', '(none)'] as const;
const STATUS_LABEL: Record<string, string> = {
  종결: '종결',
  검사검수요청: '검사검수요청',
  취소: '취소',
  '매입계산서 보류': '매입계산서 보류',
  '(none)': '미상태',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  종결: 'bg-emerald-100 text-emerald-700',
  검사검수요청: 'bg-amber-100 text-amber-700',
  취소: 'bg-slate-100 text-slate-500',
  // 2026-05-29: 핑크(rose) → 보라(violet) 변경. 납기지연 셀 색상(rose-200)과 같은 계열이라
  // 매입계산서 보류건이 없을 때도 핑크가 보여 시각적 혼동 발생. 명확히 구분되는 색으로.
  '매입계산서 보류': 'bg-violet-100 text-violet-700',
};

// 상태별 행 배경 — 종결/취소=회색, 검사검수요청=옅은파랑, 매입계산서보류=옅은보라, 그외=기본.
function statusRowClass(bellStatus: string | null | undefined): string {
  switch (bellStatus) {
    case '종결':
      return 'bg-slate-100 text-slate-500';
    case '취소':
      return 'bg-slate-100 text-slate-400 line-through';
    case '검사검수요청':
      return 'bg-sky-50';
    case '매입계산서 보류':
      return 'bg-violet-50';
    default:
      return '';
  }
}

// 납기 임박도 — 미종결/미취소이면서 납품완료일이 아직 없는 행에만.
// D-10 이내=soon(노랑), D-0 이상=overdue(빨강).
function deliveryUrgency(
  deliveryDue: string | null | undefined,
  bellStatus: string | null | undefined,
  deliveryCompletedDate: string | null | undefined,
): 'overdue' | 'soon' | null {
  if (!deliveryDue) return null;
  if (bellStatus === '종결' || bellStatus === '취소') return null;
  if (deliveryCompletedDate) return null;
  const due = new Date(deliveryDue + 'T00:00:00');
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((due.getTime() - today.getTime()) / 86_400_000);
  if (diffDays <= 0) return 'overdue';
  if (diffDays <= 10) return 'soon';
  return null;
}

const DELIVERY_CELL_CLASS: Record<'overdue' | 'soon', string> = {
  overdue: 'bg-rose-200 text-rose-900 font-semibold',
  soon: 'bg-amber-200 text-amber-900 font-medium',
};

// 국세청 매칭 마크 — 국세청 매입자료 업로드에서 이 건의 매입액과 매칭(승인번호 기록)된 경우 표시.
// 매입관리 목록의 국세청 마크와 동일한 로고 사용.
function NtsMark({ approvalNo }: { approvalNo: string | null }) {
  if (!approvalNo) return null;
  return (
    <img
      src="/nts-logo.webp"
      alt="국세청"
      className="ml-1 h-4 w-auto inline-block align-middle"
      title={`국세청 매입계산서 매칭됨 (승인번호 …${approvalNo.slice(-8)})`}
    />
  );
}

function fmtKrw(n: number): string {
  return n.toLocaleString('ko-KR');
}

/* ───────────────────── 컬럼 정의 ─────────────────────
 * 18 운영 엑셀 컬럼 + 1 시스템 (담당). 사용자 토글로 표시·숨김.
 * 기본 표시 = 7개 (★ 표시).
 */
interface BellColumnDef extends Column<EmergencyBellRow> {
  /** 사용자 토글에서 보일 라벨 (label 과 다를 수도 있음 — 짧은 라벨일 때) */
  toggleLabel?: string;
  /** 기본 표시 여부 (사용자가 preferences 미설정일 때) */
  defaultOn: boolean;
}

const BELL_COLUMN_DEFS: BellColumnDef[] = [
  {
    key: 'procurementNo', label: '납품요구번호', type: 'text', defaultOn: true,
    accessor: r => r.procurementNo ?? '',
    render: r => <span className="font-mono text-xs text-slate-700">{r.procurementNo ?? '-'}</span>,
  },
  {
    key: 'contractDate', label: '계약일', type: 'date', defaultOn: false,
    accessor: r => r.contractDate ?? '',
    render: r => (
      <span className="text-slate-500 text-xs inline-flex items-center gap-1">
        {r.contractDate ?? '-'}
        {r.purchaseOrderCount > 0 && <BellPurchaseOrderIcon bellId={r.bellId} />}
      </span>
    ),
  },
  {
    key: 'endUser', label: '수요기관', type: 'text', defaultOn: true,
    accessor: r => r.endUser ?? '',
    render: r => <span className="font-medium">{r.endUser ?? '-'}</span>,
  },
  {
    key: 'department', label: '담당과', type: 'text', defaultOn: false,
    accessor: r => r.department ?? '',
    render: r => <span className="text-slate-600 text-xs">{r.department ?? '-'}</span>,
  },
  {
    key: 'siteName', label: '공사명', type: 'text', defaultOn: true,
    accessor: r => r.siteName ?? '',
    render: r => <span className="text-xs text-slate-700">{r.siteName ?? '-'}</span>,
  },
  {
    key: 'deviceCount', label: '수량', type: 'number', align: 'right', defaultOn: true,
    accessor: r => r.deviceCount,
    render: r => <span className="num">{r.deviceCount}</span>,
  },
  {
    key: 'salesAmount', label: '금액', type: 'number', align: 'right', defaultOn: true,
    accessor: r => r.salesAmount,
    render: r => <span className="num font-semibold text-blue-700">{fmtKrw(r.salesAmount)}</span>,
  },
  {
    key: 'deliveryDue', label: '납품기한', type: 'date', defaultOn: false,
    accessor: r => r.deliveryDue ?? '',
    render: r => <span className="text-slate-500 text-xs">{r.deliveryDue ?? '-'}</span>,
  },
  {
    key: 'startDate', label: '착수일자', type: 'date', defaultOn: false,
    accessor: r => r.startDate ?? '',
    render: r => <span className="text-slate-500 text-xs">{r.startDate ?? '-'}</span>,
  },
  {
    key: 'deliveryCompletedDate', label: '납품완료일', type: 'date', defaultOn: false,
    accessor: r => r.deliveryCompletedDate ?? '',
    render: r => <span className="text-slate-500 text-xs">{r.deliveryCompletedDate ?? '-'}</span>,
  },
  {
    key: 'bellStatus', label: '종결여부', type: 'text', align: 'center', defaultOn: true,
    accessor: r => r.bellStatus ?? '',
    render: r => {
      const s = r.bellStatus;
      if (!s) return <span className="text-slate-500 text-xs">-</span>;
      const cls = STATUS_BADGE_CLASS[s] ?? 'bg-slate-100 text-slate-600';
      return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{s}</span>;
    },
  },
  {
    key: 'documents', label: '제출서류', type: 'text', defaultOn: false,
    accessor: r => r.documents ?? '',
    render: r => <span className="text-xs text-slate-500">{r.documents ?? '-'}</span>,
  },
  {
    key: 'note', label: '비고', type: 'text', defaultOn: false,
    accessor: r => r.note ?? '',
    render: r => <span className="text-xs text-slate-500">{r.note ?? '-'}</span>,
  },
  {
    key: 'paymentDate', label: '매입지급일', type: 'date', defaultOn: false,
    accessor: r => r.paymentDate ?? '',
    render: r => <span className="text-slate-500 text-xs">{r.paymentDate ?? '-'}</span>,
  },
  {
    key: 'purchaseInvoiceDate', label: '매입세금계산서', type: 'date', defaultOn: false,
    accessor: r => r.purchaseInvoiceDate ?? '',
    render: r => (
      <span className="text-slate-500 text-xs">
        {r.purchaseInvoiceDate ?? '-'}
        <NtsMark approvalNo={r.ntsApprovalNo} />
      </span>
    ),
  },
  {
    key: 'purchaseAmount', label: '매입합계', type: 'number', align: 'right', defaultOn: false,
    accessor: r => r.purchaseAmount,
    render: r => (
      <span className="num text-slate-600">
        {fmtKrw(r.purchaseAmount)}
        <NtsMark approvalNo={r.ntsApprovalNo} />
      </span>
    ),
  },
  {
    key: 'invoiceDate', label: '매출세금계산서', type: 'date', defaultOn: true,
    accessor: r => r.invoiceDate ?? '',
    render: r => (
      <span className={r.invoiceDate ? 'text-blue-600 text-xs font-medium' : 'text-slate-500 text-xs'}>
        {r.invoiceDate ?? '-'}
      </span>
    ),
  },
  {
    key: 'paidDate', label: '입금일', type: 'date', defaultOn: false,
    accessor: r => r.paidDate ?? '',
    render: r => <span className="text-slate-500 text-xs">{r.paidDate ?? '-'}</span>,
  },
  {
    key: 'profit', label: '이익', type: 'number', align: 'right', defaultOn: false,
    accessor: r => r.profit,
    render: r => <span className="num text-slate-600">{fmtKrw(r.profit)}</span>,
  },
  {
    key: 'splitDelivery', label: '분납서', type: 'text', align: 'center', defaultOn: true,
    accessor: r => r.splitDeliveryCount > 0 ? '있음' : '없음',
    render: r => <BellSplitDeliveryCell row={r} />,
  },
];

// 연번(seqNo) 정렬용 의사 컬럼 — 리스트에는 별도 th 로 렌더되지만(토글 대상 아님),
// 정렬은 applyColState 경유라 정렬 대상 배열에 포함시켜야 동작한다.
const SEQ_NO_SORT_COL: BellColumnDef = {
  key: 'seqNo', label: '연번', type: 'number', defaultOn: false,
  accessor: r => r.seqNo,
};

/** 비상벨 — 발주서 PDF 아이콘 (계약일 셀 안). 1건당 1개. 클릭 즉시 다운로드. */
function BellPurchaseOrderIcon({ bellId }: { bellId: number }) {
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    const list = await listAttachments('bell', String(bellId), '발주서');
    if (list[0]) previewAttachment(list[0]);
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="발주서 PDF 열기"
      className="text-blue-600 hover:text-blue-800"
    >
      📑
    </button>
  );
}

/** 비상벨 — 분할납품요구서 PDF 셀 (영업의 SplitDeliveryCell 과 동일 패턴). */
function BellSplitDeliveryCell({ row }: { row: EmergencyBellRow }) {
  const [showPicker, setShowPicker] = useState(false);
  if (row.splitDeliveryCount === 0) {
    return <span className="text-slate-400 text-xs">-</span>;
  }
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (row.splitDeliveryCount === 1) {
      const list = await listAttachments('bell', String(row.bellId), '분할납품요구서');
      if (list[0]) previewAttachment(list[0]);
    } else {
      setShowPicker(true);
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        title={`PDF ${row.splitDeliveryCount}개 — 클릭하여 열기`}
        className="text-rose-600 hover:text-rose-800 text-base"
      >
        📄
      </button>
      {showPicker && (
        <Suspense fallback={null}>
          <BellAttachmentDownloadModal
            parentId={String(row.bellId)}
            onClose={() => setShowPicker(false)}
          />
        </Suspense>
      )}
    </>
  );
}


export function EmergencyBellTab() {
  const { user } = useAuth();
  const canMutate = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const qc = useQueryClient();
  const [editingRow, setEditingRow] = useState<BellSalesEditInitial | null>(null);
  // 신규 매출 등록 모달 (2026-05-17 매출분석에서 이전).
  const [creating, setCreating] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);

  // ── 📐 내 표 설정 — 사용자 환경설정 로드 + hook
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: prefsApi.getPreferences,
    staleTime: 5 * 60 * 1000,
  });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  const t = useExcelModeTable({
    prefs: prefsQ.data,
    fullColumns: BELL_COLUMN_DEFS as unknown as (Column<unknown> & { defaultOn: boolean })[],
    prefsKey: {
      excelMode: 'bellTabExcelMode',
      columns: 'bellTabColumns',
      columnsKnown: 'bellTabColumnsKnown',
      order: 'bellTabColumnOrder',
      widths: 'bellTabColumnWidths',
    },
    patch: (next) => patchPrefs.mutate(next),
  });
  const visibleColumns = t.visibleColumns as unknown as BellColumnDef[];

  // ── 검색·필터 상태
  const [q, setQ] = useState('');
  const [selectedYears, setSelectedYears] = useState<Set<string>>(new Set());
  const yearsInitializedRef = useRef(false);
  const [statusActive, setStatusActive] = useState<Set<string>>(new Set(STATUS_VALUES));
  const [invoiceOnly, setInvoiceOnly] = useState(false);
  const colState = useColState();
  const [colMenuOpen, setColMenuOpen] = useState(false);

  const yearsQ = useQuery({
    queryKey: ['bell', 'years'],
    queryFn: bellApi.listBellYears,
    staleTime: 5 * 60 * 1000,
  });
  const availableYears = yearsQ.data ?? [];

  useEffect(() => {
    if (!yearsInitializedRef.current && availableYears.length > 0) {
      // 첫 진입은 최신 연도만 선택 (백엔드가 DESC 정렬로 반환)
      setSelectedYears(new Set([availableYears[0]!]));
      yearsInitializedRef.current = true;
    }
  }, [availableYears]);

  const listQ = useQuery({
    queryKey: ['bell', { q }],
    queryFn: () => bellApi.listBell({ pageSize: FETCH_LIMIT, q: q || undefined }),
    placeholderData: keepPreviousData,
  });

  const baseRows = listQ.data?.data ?? [];

  // 클라이언트 측 필터 — 연도, status, invoiceOnly
  const filtered = useMemo(() => {
    if (!yearsInitializedRef.current) return baseRows;
    return baseRows.filter(r => {
      // 연도 (contract_date 의 YEAR — 운영 엑셀의 시트 연도와 동일)
      //   계약일이 없으면 발행일의 연도 · 둘 다 없으면 **늘 보인다** (v2.231.0 · 점검 D-14 —
      //   전에는 계약일 없는 줄이 연도를 전부 켜도 사라져 「250 / 252」로 보였고, 없는 줄로 알고 두 번 넣을 뻔했다)
      if (selectedYears.size > 0 && availableYears.length > 0) {
        const d = r.contractDate ?? r.invoiceDate;
        if (d && !selectedYears.has(d.slice(0, 4))) return false;
      }
      // status
      const sKey = r.bellStatus ?? '(none)';
      if (statusActive.size > 0 && !statusActive.has(sKey)) return false;
      // 발행만
      if (invoiceOnly && !r.invoiceDate) return false;
      return true;
    });
  }, [baseRows, selectedYears, statusActive, invoiceOnly, availableYears]);

  // 연번 정렬을 위해 seqNo 의사 컬럼을 정렬 대상에 포함 (렌더는 별도 th 로 유지).
  const sortColumns = useMemo(
    () => [SEQ_NO_SORT_COL, ...visibleColumns],
    [visibleColumns],
  );
  const visibleRows = useMemo(
    () => applyColState(filtered, sortColumns, colState),
    [filtered, sortColumns, colState],
  );

  // 연번 헤더 정렬 표시 상태
  const seqSortIdx = colState.sortStack.findIndex((s) => s.key === 'seqNo');
  const seqSortDir = seqSortIdx >= 0 ? colState.sortStack[seqSortIdx]!.dir : null;
  const seqIsPrimary = seqSortIdx === 0;

  function toggleYear(y: string) {
    const next = new Set(selectedYears);
    if (next.has(y)) next.delete(y);
    else next.add(y);
    setSelectedYears(next);
  }

  function toggleStatus(s: string) {
    const next = new Set(statusActive);
    if (next.has(s)) next.delete(s);
    else next.add(s);
    setStatusActive(next);
  }

  // 삭제 mutation
  const deleteMut = useMutation({
    mutationFn: (bellId: number) => quickApi.deleteBellSales(bellId),
    onSuccess: () => syncAllData(qc),
  });

  // Row.memo 효과를 위해 stable 콜백.
  const handleEdit = useCallback((row: EmergencyBellRow) => {
    setEditingRow({
      bellId: row.bellId,
      empId: row.empId,
      companyId: row.companyId,
      siteName: row.siteName,
      endUser: row.endUser,
      contractCompany: row.contractCompany,
      deviceCount: row.deviceCount,
      unitPrice: row.unitPrice,
      salesAmount: row.salesAmount,
      purchaseAmount: row.purchaseAmount,
      invoiceDate: row.invoiceDate,
      revenueYear: row.revenueYear,
      revenueMonth: row.revenueMonth,
      maintenancePeriod: row.maintenancePeriod,
      note: row.note,
      // ETL 보강 11 필드
      procurementNo: row.procurementNo,
      contractDate: row.contractDate,
      department: row.department,
      deliveryDue: row.deliveryDue,
      startDate: row.startDate,
      deliveryCompletedDate: row.deliveryCompletedDate,
      bellStatus: toBellStatus(row.bellStatus),
      documents: row.documents,
      purchaseInvoiceDate: row.purchaseInvoiceDate,
      paymentDate: row.paymentDate,
      paidDate: row.paidDate,
    });
  }, []);

  const handleDelete = useCallback((row: EmergencyBellRow) => {
    const label = row.siteName || row.endUser || `#${row.bellId}`;
    const amount = fmtKrw(row.salesAmount);
    // 첨부 서류(발주서·검수조서 등)도 서버가 같이 지운다 (v2.232.0 · 점검 ③ D-12)
    if (!window.confirm(`이 비상벨 행을 삭제할까요?\n\n${label} · ${amount}원\n\n삭제 후 복구할 수 없습니다. 이 행에 붙은 첨부 파일도 함께 지워집니다.`)) {
      return;
    }
    deleteMut.mutate(row.bellId, {
      onError: (e) => window.alert(`삭제 실패: ${(e as Error).message}`),
    });
  }, [deleteMut]);

  // 합계 — 가시 행(필터 후) 기준 (헤더의 발행 합계용)
  const totals = useMemo(() => {
    let totalSales = 0;
    let invoicedSales = 0;
    for (const r of visibleRows) {
      totalSales += r.salesAmount;
      if (r.invoiceDate) invoicedSales += r.salesAmount;
    }
    return { totalSales, invoicedSales };
  }, [visibleRows]);

  // 표 하단 sticky 합계 — 전체(필터 전, baseRows)와 필터 후(visibleRows) 각각.
  const footerTotals = useMemo(() => {
    let allSales = 0, allPurchase = 0;
    for (const r of baseRows) {
      allSales += r.salesAmount;
      allPurchase += r.purchaseAmount;
    }
    let fSales = 0, fPurchase = 0;
    for (const r of visibleRows) {
      fSales += r.salesAmount;
      fPurchase += r.purchaseAmount;
    }
    return {
      all: { count: baseRows.length, sales: allSales, purchase: allPurchase },
      filtered: { count: visibleRows.length, sales: fSales, purchase: fPurchase },
    };
  }, [baseRows, visibleRows]);
  const hasFilter = footerTotals.all.count !== footerTotals.filtered.count;

  return (
    <div className="space-y-3">
      {/* 헤더 — Last update 배지 + 합계 */}
      <div className="flex items-end justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center gap-3 flex-wrap">
          <LastUpdateBadge />
          <div className="text-xs text-slate-500">
            {listQ.data ? (
              <>
                총 {listQ.data.page.total}건
                {visibleRows.length !== baseRows.length && ` · 필터 후 ${visibleRows.length}건`}
              </>
            ) : '불러오는 중…'}
          </div>
          <VatNote included />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm font-semibold text-slate-700">
            금액 합계 ₩{fmtKrw(totals.totalSales)}
            <span className="text-slate-400 font-normal"> · </span>
            발행 합계 <span className="text-blue-700">₩{fmtKrw(totals.invoicedSales)}</span>
          </div>
          {canMutate && (
            <button
              type="button"
              onClick={() => {
                setExcelDownloading(true);
                try {
                  exportRowsToExcel({
                    filename: `비상벨_${dateStamp()}.xlsx`,
                    sheetName: '비상벨',
                    columns: visibleColumns,
                    rows: visibleRows,
                    totals: {
                      salesAmount: footerTotals.filtered.sales,
                      purchaseAmount: footerTotals.filtered.purchase,
                    },
                  });
                } catch (e) {
                  window.alert(`엑셀 다운로드 실패: ${(e as Error).message}`);
                } finally {
                  setExcelDownloading(false);
                }
              }}
              disabled={excelDownloading || visibleRows.length === 0}
              title="현재 화면에 보이는 컬럼·행 그대로 엑셀 다운로드"
              className={`btn ${excelDownloading ? 'btn-secondary' : 'btn-secondary'}`}
            >
              {excelDownloading ? '⏳ 다운로드 중…' : '📥 엑셀'}
            </button>
          )}
          {canMutate && (
            <button
              type="button"
              onClick={async () => {
                setExcelDownloading(true);
                try {
                  await bellApi.downloadBellStatusPdf();
                } catch (e) {
                  window.alert(`납기현황 다운로드 실패: ${(e as Error).message}`);
                } finally {
                  setExcelDownloading(false);
                }
              }}
              disabled={excelDownloading}
              title="납품기한 도래 현황 PDF — 누르는 날 기준 · 종결·취소 제외 · 납기 오름차순 · 검사검수요청 연초록 / 도래(10일 안) 노랑 / 경과 분홍"
              className={`btn ${excelDownloading ? 'btn-secondary' : 'btn-success'}`}
            >
              📄 납기현황 공유 (PDF)
            </button>
          )}
          {canMutate && (
            <MailSendButton
              compose={() => ({
                docRefs: [{ docType: 'bell-status', docId: 'today', label: '비상벨 납품기한 도래현황 (오늘 기준)' }],
                subject: mailSubject('비상벨 납기현황 송부'),
              })}
              className="px-3 py-1.5 text-sm border border-sky-300 text-sky-700 hover:bg-sky-50 rounded"
              label="✉ 납기현황 메일"
              title="납기현황 보고서 PDF 를 첨부한 메일 작성"
            />
          )}
          {canMutate && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn btn-success"
              title="비상벨 매출 행 신규 등록"
            >
              + 비상벨 매출
            </button>
          )}
        </div>
      </div>

      {/* 검색 + 필터 + 컬럼 토글 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 space-y-3">
        <SearchBar
          value={q}
          onCommit={setQ}
          placeholder="🔍 납품요구번호 / 수요기관 / 공사명 / 담당과 / 비고"
        />

        <div className="flex items-center gap-3 flex-wrap text-sm">
          <div className="flex gap-1 flex-wrap">
            {STATUS_VALUES.map((s) => {
              const on = statusActive.has(s);
              const cls = STATUS_BADGE_CLASS[s] ?? 'bg-slate-100 text-slate-500';
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  aria-pressed={on}
                  className={`px-2.5 py-1 rounded text-xs transition ${
                    on ? cls + ' shadow-sm font-medium' : 'bg-slate-50 text-slate-400 line-through hover:bg-slate-100'
                  }`}
                  title={on ? '클릭하여 숨기기' : '클릭하여 표시'}
                >
                  {STATUS_LABEL[s]}
                </button>
              );
            })}
          </div>

          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={invoiceOnly}
              onChange={e => setInvoiceOnly(e.target.checked)}
            />
            <span>📜 매출세금계산서 발행건만</span>
          </label>

          {/* 연도 필터 */}
          {availableYears.length > 1 && (
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-slate-500">연도</span>
              <div className="flex gap-1">
                {availableYears.map(y => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => toggleYear(y)}
                    className={`px-2 py-0.5 text-xs rounded ${
                      selectedYears.has(y)
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200'
                    }`}
                  >{y}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 우측 버튼 묶음 — 필터·정렬 초기화 + 📐 내 표 설정 */}
        <div className="flex items-center justify-end gap-2 text-xs border-t border-slate-100 pt-2 flex-wrap">
          {(colState.sortKey ||
            Object.keys(colState.filters).length > 0 ||
            q ||
            statusActive.size !== STATUS_VALUES.length ||
            invoiceOnly ||
            (availableYears.length > 0 &&
              selectedYears.size > 0 &&
              selectedYears.size !== availableYears.length)) && (
            <ResetButton
              onClick={() => {
                colState.resetAll();
                setQ('');
                setStatusActive(new Set(STATUS_VALUES));
                setInvoiceOnly(false);
                if (availableYears.length > 0) {
                  setSelectedYears(new Set(availableYears));
                }
              }}
              label="↺ 필터·정렬 초기화"
              title="검색·상태·계산서 발행·연도·컬럼 정렬/필터 모두 초기 상태로"
            />
          )}
          <ExcelModeGroup
            enabled={t.excelMode}
            hasPrefs={t.hasPrefs}
            userName={user?.name ?? '사용자'}
            onChange={t.setExcelMode}
            onReset={t.handleReset}
          />
        </div>

        {/* ⚙ 표시 컬럼 — 📐 내 표 설정 ON 일 때만. 모바일 hidden. */}
        {t.excelMode && (
          <div className="hidden md:block border-t border-slate-100 pt-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <button
                type="button"
                onClick={() => setColMenuOpen(v => !v)}
                className="text-blue-600 hover:underline"
              >
                ⚙ 표시 컬럼 ({visibleColumns.length}/{t.fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''})
                {colMenuOpen ? ' ▲' : ' ▼'}
              </button>
              <span className="text-xs text-slate-500">
                💡 컬럼 헤더 끌어 이동 / 우측 끝 끌어 너비 조정 / 헤더 ◀ ▶ 한 칸씩 이동
              </span>
            </div>
            {colMenuOpen && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1">
                {BELL_COLUMN_DEFS.map((d) => {
                  const on = t.activeColKeys.has(d.key);
                  return (
                    <label
                      key={d.key}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${
                        on ? 'bg-blue-50 text-blue-800' : 'text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => t.toggleColumn(d.key)}
                        disabled={patchPrefs.isPending}
                      />
                      <span>{d.toggleLabel ?? d.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 표 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 overflow-x-auto">
        <table className="w-full text-[13px] border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-20">
            <tr className="text-slate-700">
              {visibleColumns.length > 0 && (
                <th className="px-2 py-2 text-center text-xs text-slate-500 font-medium whitespace-nowrap w-12">
                  <button
                    type="button"
                    onClick={() => colState.toggleSort('seqNo')}
                    className="inline-flex items-center gap-0.5 hover:text-blue-700"
                    title="연번 정렬 (오름→내림→해제)"
                  >
                    연번
                    {seqSortDir && (
                      <span className={`text-[11px] ${seqIsPrimary ? 'text-blue-600 font-bold' : 'text-slate-400'}`}>
                        {seqSortDir === 'asc' ? '▲' : '▼'}
                        {!seqIsPrimary && <sub className="ml-0.5">{seqSortIdx + 1}</sub>}
                      </span>
                    )}
                  </button>
                </th>
              )}
              {visibleColumns.map((col, idx) => (
                <ColumnHeader
                  key={col.key}
                  column={col}
                  baseRows={filtered}
                  // 연쇄 필터 — applyColState 와 같은 컬럼 집합
                  columns={sortColumns}
                  colState={colState}
                  excelMode={t.excelMode}
                  onReorder={t.excelMode ? t.handleReorder : undefined}
                  onMoveLeft={t.excelMode && idx > 0 ? () => t.handleMoveLeft(col.key) : undefined}
                  onMoveRight={
                    t.excelMode && idx < visibleColumns.length - 1
                      ? () => t.handleMoveRight(col.key)
                      : undefined
                  }
                  onResize={t.excelMode ? t.handleResize : undefined}
                  widthPx={t.columnWidths?.[col.key]}
                />
              ))}
              {canMutate && visibleColumns.length > 0 && (
                <th className="px-3 py-2 text-center text-xs text-slate-500 font-medium whitespace-nowrap">관리</th>
              )}
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && !listQ.data && (
              <tr>
                <td colSpan={(visibleColumns.length || 1) + (visibleColumns.length > 0 ? 1 : 0) + (canMutate ? 1 : 0)} className="text-center py-8 text-slate-400">
                  불러오는 중…
                </td>
              </tr>
            )}
            {listQ.isError && (
              <tr>
                <td colSpan={(visibleColumns.length || 1) + (visibleColumns.length > 0 ? 1 : 0) + (canMutate ? 1 : 0)} className="text-center py-8 text-red-500">
                  오류: {(listQ.error as Error).message}
                </td>
              </tr>
            )}
            {!listQ.isLoading && visibleRows.length === 0 && visibleColumns.length > 0 && (
              <tr>
                <td colSpan={visibleColumns.length + 1 + (canMutate ? 1 : 0)} className="text-center py-8 text-slate-400">
                  조건에 맞는 데이터가 없습니다.
                </td>
              </tr>
            )}
            {visibleColumns.length === 0 && (
              <tr>
                <td className="text-center py-8 text-slate-400 text-sm">
                  표시할 컬럼이 없습니다. 위쪽 [⚙ 표시 컬럼] 에서 하나 이상 선택하세요.
                </td>
              </tr>
            )}
            {visibleColumns.length > 0 && visibleRows.map((row) => (
              <BellRow
                key={row.bellId}
                row={row}
                rowNo={row.seqNo}
                visibleColumns={visibleColumns}
                canMutate={canMutate}
                onEdit={handleEdit}
                onDelete={handleDelete}
                deletePending={deleteMut.isPending}
              />
            ))}
          </tbody>
          {visibleColumns.length > 0 && visibleRows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-slate-100 border-t-2 border-slate-300 z-10">
              <tr className="text-xs text-slate-700">
                <td
                  colSpan={visibleColumns.length + 1 + (canMutate ? 1 : 0)}
                  className="px-3 py-2"
                >
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="font-medium text-slate-800">
                      전체 {footerTotals.all.count}건 · 매출 ₩{fmtKrw(footerTotals.all.sales)} · 매입 ₩{fmtKrw(footerTotals.all.purchase)}
                    </span>
                    {hasFilter && (
                      <span className="text-blue-700 font-medium">
                        필터 후 {footerTotals.filtered.count}건 · 매출 ₩{fmtKrw(footerTotals.filtered.sales)} · 매입 ₩{fmtKrw(footerTotals.filtered.purchase)}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        ※ 비상벨 금액(단가/매출액/매입액/이익)은 모두 <b>부가세 포함</b> 기준입니다 (분납서 금액 그대로 — 2026-07-13 전환).
        매출분석·대시보드에는 자동으로 부가세 별도(÷1.1)로 환산되어 집계됩니다.
      </p>
      <p className="text-xs text-slate-500">
        ※ 매출분석의 비상벨 매출 합계는 매출세금계산서(invoiceDate) 발행된 건만, invoiceDate 기준 연·월에 합산됩니다.
      </p>

      {/* lazy 모달 — [+ 비상벨 매출] / ✎ 수정 시 처음 로드 */}
      <Suspense fallback={null}>
        {creating && (
          <BellSalesQuickModal
            onClose={() => setCreating(false)}
            onSaved={() => setCreating(false)}
          />
        )}
        {editingRow && (
          <BellSalesQuickModal
            editing={editingRow}
            onClose={() => setEditingRow(null)}
            onSaved={() => setEditingRow(null)}
          />
        )}
      </Suspense>
    </div>
  );
}

// React.memo — 부모 렌더 시 다른 행 재렌더 skip. visibleColumns 는 activeColKeys 가
// 변하지 않으면 reference stable (useMemo). onEdit/onDelete 도 useCallback 으로 stable.
const BellRow = memo(function BellRow({
  row,
  rowNo,
  visibleColumns,
  canMutate,
  onEdit,
  onDelete,
  deletePending,
}: {
  row: EmergencyBellRow;
  rowNo: number;
  visibleColumns: BellColumnDef[];
  canMutate: boolean;
  onEdit: (row: EmergencyBellRow) => void;
  onDelete: (row: EmergencyBellRow) => void;
  deletePending: boolean;
}) {
  const rowBg = statusRowClass(row.bellStatus);
  const urgency = deliveryUrgency(row.deliveryDue, row.bellStatus, row.deliveryCompletedDate);
  return (
    <tr
      className={`border-b border-slate-100 hover:bg-slate-50 ${rowBg}${canMutate ? ' cursor-pointer' : ''}`}
      onClick={canMutate ? () => onEdit(row) : undefined}
      title={canMutate ? '클릭하여 수정' : undefined}
    >
      <td className="px-2 py-2 text-center text-xs text-slate-500 border-r border-slate-100 num">
        {rowNo}
      </td>
      {visibleColumns.map(col => {
        const align =
          col.align === 'right' ? 'text-right'
            : col.align === 'center' ? 'text-center'
            : 'text-left';
        const cellExtra =
          col.key === 'deliveryDue' && urgency ? ` ${DELIVERY_CELL_CLASS[urgency]}` : '';
        return (
          <td
            key={col.key}
            className={`px-2 py-1 border-b border-slate-200 ${align}${cellExtra}`}
            title={
              col.key === 'deliveryDue' && urgency === 'overdue'
                ? '납기일 도달/경과 (미종결)'
                : col.key === 'deliveryDue' && urgency === 'soon'
                  ? '납기 10일 이내 (미종결)'
                  : undefined
            }
          >
            {col.render ? col.render(row) : String(col.accessor(row) ?? '')}
          </td>
        );
      })}
      {canMutate && (
        <td className="px-2 py-1.5 text-center whitespace-nowrap">
          <div className="inline-flex gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(row); }}
              className="btn btn-secondary btn-sm"
              title="수정"
            >
              ✎
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(row); }}
              disabled={deletePending}
              className="btn btn-secondary btn-sm"
              title="삭제"
            >
              🗑
            </button>
          </div>
        </td>
      )}
    </tr>
  );
});
